import { Dangerous, Email, Error, Groups, Person, Visibility } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { ISelectedCandidate } from "../../App/State/Slices/MessageDialogSlice";
import { BaseFormField } from "../../App/Typings/Campaigns";
import { ICandidate, ICandidateEmailAddress, ICandidatePhoneNumber } from "../../App/Typings/Candidate";
import { ICandidateProfile } from "../../App/Typings/CandidateProfile";
import { IGroup } from "../../App/Typings/Groups";
import { ICandidateWithMessageInfo, MessageType } from "../../App/Typings/MessageText";
import { IMessageContainers } from "../Enums/MessageEnums";

export const truncateMessage = (input: string, maxLength?: number, hideEllipse?: boolean) => {
  const length = maxLength || 100;
  const suffix = hideEllipse ? "" : "...";
  if (input.length > length) {
    return `${input.substring(0, length)}${suffix}`;
  }
  return input;
};

export const buildUniqueKey = (type: string) => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  const values = crypto.getRandomValues(new Uint32Array(12));
  const x = Array.from(values, (v) => chars[v % chars.length]).join("");
  return type + "-" + x;
};

export const buildTitleHandler = (action: string, groupName?: string) => {
  if (action === "sendgroupmessage") {
    return "Message: " + groupName;
  } else if (action === "groupmessagehistory") {
    return "Message History: " + groupName;
  } else if (action === "groupmessageschedule") {
    return "Messages Scheduled: " + groupName;
  } else {
    return "Message Group";
  }
};

export const buildSubTitleHandler = (selectedCandidateIds: string[], action: string) => {
  if (action === "sendgroupmessage") {
    return "Candidates: " + selectedCandidateIds.length;
  } else if (action === "groupmessagehistory") {
    return "";
  } else if (action === "groupmessageschedule") {
    return "";
  }
  return "No Candidates Selected";
};

export const buildScheduleMessageToolTip = (count?: string) => {
  const msgCount = parseInt(count?.valueOf() ?? "0");
  if (msgCount > 0) {
    return "View [" + msgCount + "] scheduled message(s)";
  }
  return "No current scheduled message(s) to view.";
};

export const buildGroupMessageHistoryTooltip = (count?: string) => {
  const msgCount = parseInt(count?.valueOf() ?? "0");
  if (msgCount > 0) {
    return "View [" + msgCount + "] sent group message(s).";
  }
  return "No current group messages to view.";
};

export const getMessageDisplayType = (messageType: string) => {
  switch (messageType) {
    case "CampaignTextMessage":
    case "GroupEmailMessage":
    case "CampaignEmailMessage":
    case "DirectTextMessage":
    case "DirectEmailMessage":
    case "GroupTextMessage":
    case "CandidateTextMessage":
    case "SystemTextMessage":
    case "SystemEmailMessage":
      return "send";
    default:
      return "reply";
  }
};

interface IDynamicIconProps {
  messageType: MessageType;
  user: string;
  groupName?: string;
  errorMessage?: string;
}

interface IMessageIcon {
  tooltip: (props: IDynamicIconProps) => string;
  icon: JSX.Element;
}

const MessageIconDictionary = {
  DirectEmailMessage: {
    tooltip: (props) => props.user + " sent a direct email message",
    icon: <Email />,
  },
  DirectTextMessage: {
    tooltip: (props) => props.user + " sent a direct text message",
    icon: <Person />,
  },
  GroupEmailMessage: {
    tooltip: (props) =>
      props.user +
      (props.groupName
        ? " sent an email message to Group: " + props.groupName
        : " sent an email message to a group"),
    icon: <Email />,
  },
  GroupTextMessage: {
    tooltip: (props) =>
      props.user +
      (props.groupName
        ? " sent a text message to Group: " + props.groupName
        : " sent a text message to a group"),
    icon: <Groups />,
  },
  CampaignEmailMessage: {
    tooltip: (props) => props.user + " sent an email to a form group",
    icon: <Groups />,
  },
  CampaignTextMessage: {
    tooltip: (props) => props.user + " sent a text message to a form group",
    icon: <Groups />,
  },
  SystemEmailMessage: {
    tooltip: () => "System generated email message",
    icon: <Email />,
  },
  SystemTextMessage: {
    tooltip: () => "System generated text message",
    icon: <Person />,
  },
  Stop: {
    tooltip: (props) => "Candidate has opted out of receiving messages",
    icon: <Dangerous color="error" />,
  },
  ERROR: {
    tooltip: (props) => props.errorMessage || "Unknown Error",
    icon: <Error color="error" />,
  },
  Read: {
    tooltip: (props) => props.user,
    icon: <Visibility />,
  },
  ReplyText: {
    tooltip: (props) => props.user,
    icon: <Person />,
  },
  ReplyTextMessage: {
    tooltip: (props) => props.user,
    icon: <Person />,
  },
  ReplyEmailMessage: {
    tooltip: (props) => props.user,
    icon: <Person />,
  },
} as Record<MessageType, IMessageIcon>;

export const DynamicMessageIcon = (props: IDynamicIconProps) => {
  const messageIconData = MessageIconDictionary[props.messageType] || MessageIconDictionary.ERROR;
  const Icon = messageIconData.icon;
  const tooltipTitle = messageIconData.tooltip(props);
  return <Tooltip title={tooltipTitle || "Unknown"}>{Icon}</Tooltip>;
};

export const getMessageContainerDisplayName = (
  container: IMessageContainers,
  selection?: ISelectedCandidate[],
) => {
  let subject = "Candidates";
  const selectionCount = selection?.length || 0;
  if (selectionCount > 0) {
    const firstCandidateName = `${selection![0].firstName} ${selection![0].lastName}`;
    subject = selectionCount === 1 ? firstCandidateName : `${selectionCount} Candidates`;
  }
  switch (container) {
    case IMessageContainers.CandidateScheduledText:
      return "Schedule Text";
    case IMessageContainers.GroupScheduledText:
      return "Schedule Group Text";
    case IMessageContainers.CandidateScheduledEmail:
      return "Schedule Email";
    case IMessageContainers.GroupScheduledEmail:
      return "Schedule Group Email";
    case IMessageContainers.CandidateTextNow:
      return "Send Text Now";
    case IMessageContainers.GroupTextNow:
      return "Send Group Text Now";
    case IMessageContainers.CandidateEmailNow:
      return "Send Email Now";
    case IMessageContainers.GroupEmailNow:
      return "Send Group Email Now";
    case IMessageContainers.BulkTextNow:
      return `Text ${subject} Now`;
    case IMessageContainers.BulkEmailNow:
      return `Email ${subject} Now`;
    case IMessageContainers.BulkScheduledText:
      return `Schedule Text for ${subject}`;
    case IMessageContainers.BulkScheduledEmail:
      return `Schedule Email for ${subject}`;
    default:
      return "Send Message";
  }
};

export const IsEmailMessage = (containerType: IMessageContainers) => {
  switch (containerType) {
    case IMessageContainers.CandidateEmailNow:
    case IMessageContainers.CandidateScheduledEmail:
    case IMessageContainers.GroupEmailNow:
    case IMessageContainers.GroupScheduledEmail:
      return true;
    default:
      return false;
  }
};

export const IsTextMessage = (containerType: IMessageContainers) => {
  switch (containerType) {
    case IMessageContainers.CandidateTextNow:
    case IMessageContainers.CandidateScheduledText:
    case IMessageContainers.GroupTextNow:
    case IMessageContainers.GroupScheduledText:
      return true;
    default:
      return false;
  }
};

export const GetContainerTypeByTabNumber = (tab: number, isGroup = true) => {
  switch (tab) {
    case 0:
      return isGroup ? IMessageContainers.GroupTextNow : IMessageContainers.BulkTextNow;
    case 1:
      return isGroup ? IMessageContainers.GroupEmailNow : IMessageContainers.BulkEmailNow;
    case 2:
      return isGroup ? IMessageContainers.GroupScheduledText : IMessageContainers.BulkScheduledText;
    case 3:
      return isGroup ? IMessageContainers.GroupScheduledEmail : IMessageContainers.BulkScheduledEmail;
    default:
      return 0;
  }
};

export const GetContainerTabNumberByType = (containerType: IMessageContainers) => {
  switch (containerType) {
    case IMessageContainers.GroupTextNow:
      return 0;
    case IMessageContainers.GroupEmailNow:
      return 1;
    case IMessageContainers.GroupScheduledText:
      return 2;
    case IMessageContainers.GroupScheduledEmail:
      return 3;
    default:
      return 0;
  }
};

export const DisableSendEmailButton = (subject: string, body: any, mutateLoading: boolean) => {
  return mutateLoading || subject.length === 0 || body.length === 0;
};
export const DisableAddButton = (
  container: IMessageContainers,
  sendHour: string,
  sendMinute: string,
  messageBody: any,
  messageSubject: any,
  mutateLoading?: boolean,
) => {
  return (
    mutateLoading ||
    sendHour.length === 0 ||
    sendMinute.length === 0 ||
    messageBody.length === 0 ||
    (IsEmailMessage(container) && messageSubject.length === 0)
  );
};

export const DisableDirectEmailMessageAddButton = (
  container: IMessageContainers,
  sendHour: string,
  sendMinute: string,
  messageBody: any,
  messageSubject: any,
) => {
  return IMessageContainers[container].includes("Email")
    ? sendHour.length === 0 ||
        sendMinute.length === 0 ||
        messageBody.length === 0 ||
        messageSubject.length === 0
    : true;
};

export const BuildDirectMessageDialogHeader = (
  candidate?: ICandidate | ICandidateWithMessageInfo,
  candidateProfile?: ICandidateProfile,
  group?: IGroup,
) => {
  if (candidate?.id) {
    return candidate.firstName + " " + candidate.lastName;
  }
  if (candidateProfile?.id) {
    return candidateProfile.firstName + " " + candidateProfile.lastName;
  }
  if (group?.id) {
    return group.name;
  }
  return "";
};

export const BuildPhoneNumber = (
  candidate?: ICandidate | ICandidateWithMessageInfo,
  candidateProfile?: ICandidateProfile,
  group?: IGroup,
) => {
  const validNumbers = candidate?.phoneNumbers?.filter((p) => p.isValid);
  if (candidate?.id && validNumbers?.length) {
    return " " + generateTextListDisplay(validNumbers);
  }
  if (candidateProfile?.id && validNumbers?.length) {
    return " " + generateTextListDisplay(validNumbers);
  }
  if (group?.id) {
    return group.name;
  }

  return "";
};

export const BuildEmailAddress = (
  candidate?: ICandidate | ICandidateWithMessageInfo,
  candidateProfile?: ICandidateProfile,
  group?: IGroup,
) => {
  if (candidate?.id && candidate?.emailAddresses?.length) {
    return " " + generateEmailListDisplay(candidate.emailAddresses);
  }
  if (candidateProfile?.id && candidateProfile?.emailAddresses?.length) {
    return " " + generateEmailListDisplay(candidateProfile.emailAddresses);
  }
  if (group?.id) {
    return group.name;
  }

  return "";
};

const generateListDisplay = (list: string[], maxLength?: number) => {
  if (list.length === 0) {
    return "";
  }
  if (list.length === 1) {
    return list[0];
  }

  const maxDisplayLength = maxLength || 100;
  let displayString = "";
  let displayLengthSoFar = 0;
  let moreItemsCount = 0;

  for (let i = 0; i < list.length; i++) {
    const currentItem = list[i];
    const currentLength = currentItem.length + 2;

    if (i === 0 && currentLength > maxDisplayLength) {
      displayString = currentItem;
      moreItemsCount = list.length - 1;
      break;
    }
    if (displayLengthSoFar + currentLength > maxDisplayLength) {
      moreItemsCount = list.length - i;
      break;
    }
    if (displayString.length > 0) {
      displayString += ", ";
    }
    displayString += currentItem;
    displayLengthSoFar += currentLength;
  }

  if (moreItemsCount) {
    displayString += ` (+${moreItemsCount} more)`;
  }

  return displayString;
};

const generateEmailListDisplay = (emailList: ICandidateEmailAddress[]) => {
  return generateListDisplay(emailList.map((p) => p.emailAddress));
};
const generateTextListDisplay = (phoneList: ICandidatePhoneNumber[]) => {
  return generateListDisplay(phoneList.map((p) => p.phoneNumber));
};

export const TemplateCreateButtonDisableMessage = (
  name: string,
  visibility: string,
  body: string,
  mutationLoading: boolean,
  isEmail: boolean,
  subject?: string,
) => {
  if (mutationLoading) {
    return "Creating Template...";
  }
  const requiredFields = [];
  if (!name.length) {
    requiredFields.push("Template Name");
  }
  if (visibility.length === 0) {
    requiredFields.push("Visibility");
  }
  if (isEmail && (!subject || !subject.length)) {
    requiredFields.push("Subject");
  }
  if (!body.length) {
    requiredFields.push("Body");
  }

  if (requiredFields.length === 0) {
    return "";
  }
  return "Required Fields: " + requiredFields.join(" | ");
};

export const AreaOfInterestTag = "[[AreaOfInterest]]";
export const AreaOfInterestPattern = /\[\[AreaOfInterest\]\]/gi;
export const RecruiterPattern = /\[\[Recruiter\w+\]\]/gi;
export const RecruiterTitleTag = "[[RecruiterTitle]]";
export const RecruiterTitlePattern = /\[\[RecruiterTitle\]\]/gi;
